/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Form, FormCheck, FormGroup } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import Modal from '~/components/Modal'
import FsiPropertiesInlineEditor from '~/components/doc-preview/properties/FsiPropertiesInlineEditor'
import { Select } from '~/components/Select'
import _ from 'underscore'
import Utils from '~/lib/Utils'
import aclService from '~/services/acl.service'
import { getDetails } from '~/services/api/files'
import { toast } from '~/services/toast'
import { GroupType } from '~/constants/group'
import type { AclAssignment } from '~/types/acl'
import Loader from '~/components/shared/Loader'
// import ModalDialogFn from "../../../component/ModalDialogFn";
// import ServiceContext from "../../../component/ServiceContext";
// import FsiPropertiesInlineEditor from "../properties/FsiPropertiesInlineEditor";
// import { Select } from "../../../component/Select";
// import Utils from "../../../lib/Utils";
// import { GroupType } from '../../../constants/group';

interface AccessRightsModalProps {
  fsi: any
}

interface SelectOption {
  value: any
  label: string
  _uid?: string
  name?: string
  type?: string
  isFixed?: boolean
  isDisabled?: boolean
}

const AccessRightsModal: React.FC<AccessRightsModalProps> = ({ fsi }) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { constant, $rootScope } = context

  const [isOpen, setIsOpen] = useState(true)
  const [file, setFile] = useState<any | null>(null)
  const [fsiAcls, setFsiAcls] = useState<AclAssignment[]>([])
  const [fsiAclsLocal, setFsiAclsLocal] = useState<AclAssignment[]>([])
  const [show, setShow] = useState(false)
  const [recursive, setRecursive] = useState(false)
  const [onlyChanges, setOnlyChanges] = useState(true)
  const [showBtns, setShowBtns] = useState(false)
  const [groupOptionsCaRolesDisabled, setGroupOptionsCaRolesDisabled] = useState<SelectOption[] | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    fetchFileData(fsi)
  }, [])

  useEffect(() => {
    setFile(fsi)
    fetchFileData(fsi)
  }, [fsi])

  const fetchFileData = (fsi: any) => {
    if (!fsi || !fsi.id || !fsi.type || !fsi.actionFlags) {
      getDetails(fsi.id).then((data) => {
        setFile(data)
        loadAcls(data)
      })
    } else {
      loadAcls(fsi)
    }
  }

  const loadAcls = (fileData: any = cloneDeep(file)) => {
    if (fileData) {
      setIsLoading(true)
      // Use getFsiAclsLegacy to match exact behavior of AngularJS version
      aclService.getFsiAclsLegacy(fileData).then((data) => {
        if (data?.length) {
          const groupsOptionsData = data[0].availableUsersGroups
          setGroupOptionsCaRolesDisabled(
            Utils.transformToSelectOptions(groupsOptionsData, '_uid', 'name', null, true).map((option) => {
              return {
                ...option,
                ...(option?.type === GroupType.CLOUDADMIN_ACL ? { isFixed: true, isDisabled: true } : {})
              }
            })
          )
        }
        setFsiAcls(data)
        setFsiAclsLocal(data)
        setIsLoading(false)
        setShow(true)
      })
    }
  }

  const doCheck = (arg: any) => {
    const returnArray = []
    if (arg !== undefined) {
      for (let i = 0; arg.length > i; i++) {
        returnArray.push(arg[i].chName)
      }
      if (returnArray.length > 0) {
        return returnArray.join(', ')
      } else {
        return translate('ACL_NOTHING_SET')
      }
    }
  }

  const handleChange = (options: any, level: any) => {
    setShowBtns(true)
    let fsiAclsLocalData = cloneDeep(fsiAclsLocal)
    const mergedOptions = options.map((item: any) => _.omit(item, 'value', 'label'))
    fsiAclsLocalData = fsiAclsLocalData.map((item) => {
      if (item?.level === level) {
        item.merged = mergedOptions
      }
      return item
    })
    setFsiAclsLocal(fsiAclsLocalData)
  }

  const onAcceptItem = (data: any) => {
    const fsiAclsData = cloneDeep(fsiAcls)
    const itemIndex = fsiAclsLocal?.findIndex((item) => item?.level === data?.level)
    fsiAclsData[itemIndex].merged = fsiAclsLocal[itemIndex].merged
    setFsiAcls(fsiAclsData)
  }

  const onCancelItem = (data: any) => {
    const itemIndex = fsiAcls?.findIndex((item) => item?.level === data?.level)
    const fsiAclsLocalData = cloneDeep(fsiAclsLocal)
    fsiAclsLocalData[itemIndex].merged = fsiAcls[itemIndex].merged
    setFsiAclsLocal(fsiAclsLocalData)
  }

  const cancel = () => {
    loadAcls()
    setShowBtns(false)
  }

  const save = (recursively: boolean, onlyChanges: boolean) => {
    const fsiAclsData = cloneDeep(fsiAclsLocal)
    aclService.updateFsiAcls(fsiAclsData, file?.id as number, { recursively, onlyChanges }).then(
      (data: any) => {
        if (data && data._status === 208) {
          toast.info(translate('ACL_UPDATING_IN_PROGRESS_MSG'))
        } else {
          toast.success(translate('BROWSER_FILE_SETTINGS_ACLS_SUCCESS_MSG'))
          // Trigger table refresh (window event bus)
          window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        }
        setIsOpen(false)
      },
      (data: any) => {
        if (data) {
          if (data?.status === 400) {
            toast.error(translate('ACL_ERROR_MSG'))
          } else {
            toast.error(data?.message)
          }
        }
      }
    )
  }

  return (
    <Modal
      className="theme-sandman"
      show={isOpen}
      title={translate('BROWSER_FILE_SETTINGS_ACLS_H_TITLE')}
      handleClose={() => setIsOpen(false)}
    >
      <Modal.Body>
        <Loader blocking={isLoading}>
          <div className="aclsPage">
            <div className="form-group wparag w100p" style={{ marginBottom: '12px' }}>
              <label>{translate(file?.type === constant.file.type.FOLDER ? 'FOLDER' : 'DOCUMENT')}</label>
              <p className="whitespaceprewrap fs12">{file?.name}</p>
            </div>
            {show && (
              <div className="acl-table-wrapper">
                <table className="acl-table">
                  <thead>
                    <tr>
                      <th className="accessrights">{translate('ACL_TABLE_TH_ACCESS_RIGHTS')}</th>
                      <th className="usergroup" style={{ paddingLeft: '8px' }}>
                        {translate('ACL_TABLE_TH_USERS_GROUPS')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fsiAclsLocal.map((item, index) => {
                      return (
                        <tr key={index}>
                          <td className="accessrights fs12">{translate('ACL_' + item?.level)}</td>
                          {!file?.actionFlags?.canManageAcl && file?.actionFlags?.canShowAcl && (
                            <td className="usergroup fs12">{doCheck(item?.merged)}</td>
                          )}
                          {file?.actionFlags?.canManageAcl && (
                            <td className="usergroup">
                              <FsiPropertiesInlineEditor
                                isAclFlow
                                property={item as any}
                                onAccept={onAcceptItem as any}
                                onRevert={() => {}}
                                onCancel={onCancelItem as any}
                                isEditable
                                value={() => {
                                  return Utils.transformToSelectOptions(item?.merged, '_uid', 'name', null, true)
                                }}
                                input={
                                  <Select
                                    value={_.pluck(item?.merged, '_uid')}
                                    name={`ACL_${item?.level}`}
                                    placeholder={translate('ITEM(S)...')}
                                    autoFocus
                                    options={groupOptionsCaRolesDisabled ?? []}
                                    multi={true}
                                    searchable
                                    onChange={(options) => {
                                      handleChange(options, item?.level)
                                    }}
                                  />
                                }
                              />
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {file?.type === constant.file.type.FOLDER && (
              <div style={{ marginBottom: '18px' }} className="access-rights-modal-body">
                <Form.Label>{translate('ACL_LABEL_APPLIES_TO')}</Form.Label>
                <FormGroup>
                  <FormCheck
                    type="radio"
                    className="pl0 d-inline-flex gap-2"
                    name="oatat3"
                    id="input-oaat3"
                    checked={!recursive}
                    onChange={() => {
                      setRecursive(!recursive)
                    }}
                    inline
                    label={translate('ACL_APPLY_TO_THIS_FOLDER_ONLY')}
                  />
                </FormGroup>
                <FormGroup>
                  <FormCheck
                    type="radio"
                    className="pl0 d-inline-flex gap-2"
                    name="oatat3"
                    id="input-ato3"
                    checked={recursive}
                    onChange={() => {
                      setRecursive(!recursive)
                      setShowBtns(true)
                    }}
                    inline
                    label={translate('ACL_APPLY_TO_THIS_FOLDER_AND_ALL_ENCLOSED')}
                  />
                </FormGroup>
              </div>
            )}
            {file?.type === constant.file.type.FOLDER && recursive && (
              <div className="access-rights-modal-body">
                <FormGroup className="d-flex justify-content-start checkbox-group">
                  <input
                    type="checkbox"
                    id="onlyApplyChangesId"
                    name="onlyApplyChangesId"
                    onChange={() => {
                      setOnlyChanges(!onlyChanges)
                      setShowBtns(true)
                    }}
                    checked={onlyChanges}
                  />
                  <label className="checkbox-label" htmlFor="onlyApplyChangesId">
                    <div className="chkbc"></div>
                    {translate('ACL_ONLY_APPLY_CHANGES')}
                  </label>
                </FormGroup>
              </div>
            )}
          </div>
        </Loader>
      </Modal.Body>
      <Modal.Footer>
        <div>
          {showBtns && (
            <Button data-action={'cancel'} variant="link" className="text-capitalize" onClick={cancel} type="button">
              {translate('CANCEL')}
            </Button>
          )}
          {showBtns && (
            <Button
              data-action={'save'}
              onClick={() => save(recursive, onlyChanges)}
              className="btn btn-primary text-capitalize"
              type="submit"
            >
              {translate('SAVE')}
            </Button>
          )}
          {!showBtns && (
            <Button
              data-action={'ok'}
              className="btn btn-primary text-capitalize"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              {translate('OK')}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default AccessRightsModal
